import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Sparkles, Phone, MessageSquare, Mail, UserCheck } from 'lucide-react';

const OfferingsSlide = ({ onNavigate }) => {
  return (
    <div className="min-h-full xl:h-full w-full bg-slate-950 flex flex-col items-center justify-center p-4 xl:p-12 font-sans relative overflow-y-auto xl:overflow-hidden">
      
      {/* Futuristic Background Elements - Adjusted to Blue */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#3b82f620_1px,transparent_1px),linear-gradient(to_bottom,#3b82f620_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)]" />
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-600/30 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2 animate-pulse" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-blue-800/30 rounded-full blur-[120px] translate-y-1/2 -translate-x-1/2 animate-pulse" />

      <div className="max-w-7xl w-full h-full flex flex-col justify-center relative z-10">
        
        {/* Title Section */}
        <div className="text-center mb-12">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl lg:text-6xl font-bold text-white mb-6 tracking-tight drop-shadow-lg">
              What We Offer
            </h2>
            <div className="h-1 w-32 bg-gradient-to-r from-blue-500 to-blue-400 mx-auto rounded-full mb-6" />
            <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto font-light leading-relaxed">
              Two powerful AI bundles designed to revolutionize your dental practice.
            </p>
          </motion.div>
        </div>

        {/* Cards Container - Reduced gap and card size */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 xl:gap-10 h-auto w-full items-stretch max-w-5xl mx-auto pb-8 xl:pb-0">
          
          {/* Front Office Bundle Card */}
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            whileHover={{ y: -10, scale: 1.02 }}
            className="group relative bg-slate-900/60 backdrop-blur-xl rounded-[2rem] border border-blue-500/30 p-1 overflow-hidden cursor-pointer shadow-[0_0_50px_-12px_rgba(59,130,246,0.3)] hover:shadow-[0_0_50px_-6px_rgba(59,130,246,0.5)] transition-all duration-300"
            onClick={() => onNavigate('front-office')}
          >
            {/* Hover Gradient Border */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 via-transparent to-blue-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            
            <div className="relative h-full bg-slate-900/80 rounded-[1.8rem] p-6 lg:p-8 flex flex-col overflow-hidden">
              
              {/* Agent Team - Replaced single image with team avatars */}
              <div className="flex justify-between items-start mb-6 relative z-10">
                <div className="flex -space-x-4">
                  {[
                    "/assets/amy.png",
                    "/assets/leena.png", 
                    "/assets/stephanie.png",
                    "/assets/amanda.png"
                  ].map((src, i) => (
                    <div key={i} className="w-16 h-16 rounded-full overflow-hidden border-2 border-slate-900 shadow-lg relative z-10 transition-transform hover:scale-110 hover:z-20">
                      <img src={src} alt="Team Member" className="w-full h-full object-cover" />
                    </div>
                  ))}
                  <div className="w-16 h-16 rounded-full bg-blue-600 border-2 border-slate-900 flex items-center justify-center text-white font-bold text-sm shadow-lg relative z-0">
                    +4
                  </div>
                </div>
                
                <div className="bg-blue-500/20 border border-blue-500/30 backdrop-blur-md px-3 py-1 rounded-full">
                  <span className="text-xs font-semibold text-blue-300 flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3" /> Most Popular
                  </span>
                </div>
              </div>

              {/* Communication Suite Graphic */}
              <div className="mb-6 -mt-2 bg-slate-800/50 rounded-xl border border-blue-500/20 p-3 flex items-center gap-3">
                <div className="flex -space-x-2">
                   <div className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-700 flex items-center justify-center text-blue-400 shadow-sm z-30">
                     <Phone className="w-4 h-4" />
                   </div>
                   <div className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-700 flex items-center justify-center text-blue-400 shadow-sm z-20">
                     <MessageSquare className="w-4 h-4" />
                   </div>
                   <div className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-700 flex items-center justify-center text-blue-400 shadow-sm z-10">
                     <Mail className="w-4 h-4" />
                   </div>
                </div>
                <div>
                   <span className="text-xs font-bold text-white block">Full Communication Suite Included</span>
                   <span className="text-[10px] text-slate-400">VoIP, Text, Email, Forms & Campaigns</span>
                </div>
              </div>

              {/* Content */}
              <h3 className="text-2xl lg:text-3xl font-bold text-white mb-3 group-hover:text-blue-400 transition-colors tracking-tight">
                Front Office AI Bundle
              </h3>
              
              <p className="text-base text-slate-400 leading-relaxed mb-6">
                Your complete digital front desk team. Automate scheduling, calls, and patient engagement instantly.
              </p>

              <div className="space-y-3 mb-8 flex-grow">
                {[
                  "8 Specialized AI Agents",
                  "Communication & Marketing Suite",
                  "24/7 Patient Engagement"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-slate-300 font-medium text-sm">
                    <div className="w-5 h-5 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400">
                      <Check className="w-3 h-3" />
                    </div>
                    {item}
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="mt-auto">
                <div className="w-full py-3 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 text-white font-bold text-base flex items-center justify-center gap-2 group-hover:gap-3 transition-all shadow-lg group-hover:shadow-blue-500/25">
                  Explore Front Office <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Back Office Bundle Card */}
          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            whileHover={{ y: -10, scale: 1.02 }}
            className="group relative bg-slate-900/60 backdrop-blur-xl rounded-[2rem] border border-blue-500/30 p-1 overflow-hidden cursor-pointer shadow-[0_0_50px_-12px_rgba(59,130,246,0.3)] hover:shadow-[0_0_50px_-6px_rgba(59,130,246,0.5)] transition-all duration-300"
            onClick={() => onNavigate('back-office')}
          >
            {/* Hover Gradient Border */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 via-transparent to-blue-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            
            <div className="relative h-full bg-slate-900/80 rounded-[1.8rem] p-6 lg:p-8 flex flex-col overflow-hidden">
              
              {/* Agent Team - Back Office */}
              <div className="mb-6 relative z-10">
                <div className="flex -space-x-4">
                  {[
                    "/assets/ariel.png",
                    "/assets/kianna.png", 
                    "/assets/sarah.png",
                    "/assets/julia.png"
                  ].map((src, i) => (
                    <div key={i} className="w-16 h-16 rounded-full overflow-hidden border-2 border-slate-900 shadow-lg relative z-10 transition-transform hover:scale-110 hover:z-20">
                      <img src={src} alt="Team Member" className="w-full h-full object-cover" />
                    </div>
                  ))}
                  <div className="w-16 h-16 rounded-full bg-blue-600 border-2 border-slate-900 flex items-center justify-center text-white font-bold text-sm shadow-lg relative z-0">
                    +4
                  </div>
                </div>
              </div>

              {/* Human Expert Graphic */}
              <div className="mb-6 -mt-2 bg-indigo-900/20 rounded-xl border border-indigo-500/20 p-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg ring-2 ring-slate-900">
                  <UserCheck className="w-4 h-4 text-white" />
                </div>
                <div>
                   <span className="text-xs font-bold text-indigo-300 block uppercase tracking-wider">Plus Human Expert</span>
                   <span className="text-[10px] text-slate-400">For oversight & complex tasks</span>
                </div>
              </div>

              {/* Content */}
              <h3 className="text-2xl lg:text-3xl font-bold text-white mb-3 group-hover:text-blue-400 transition-colors tracking-tight">
                Back Office AI Bundle
              </h3>
              
              <p className="text-base text-slate-400 leading-relaxed mb-6">
                End-to-end revenue cycle management. Get paid faster with AI precision and human oversight.
              </p>

              <div className="space-y-3 mb-8 flex-grow">
                {[
                  "8 Dental Billing AI Agents",
                  "US-Based Human Biller",
                  "Complex Claims Handling"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-slate-300 font-medium text-sm">
                    <div className="w-5 h-5 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400">
                      <Check className="w-3 h-3" />
                    </div>
                    {item}
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="mt-auto">
                <div className="w-full py-3 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 text-white font-bold text-base flex items-center justify-center gap-2 group-hover:gap-3 transition-all shadow-lg group-hover:shadow-blue-500/25">
                  Explore Back Office <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </div>
  );
};

export default OfferingsSlide;
